/**
 * Send a socket-like event to preview/live iframes in the Producer UI
 * via postMessage. This allows tgb-live iframes using transport=message
 * to receive the same data they would get via the socket server.
 *
 * By default sends to both live and preview iframes.
 * Pass `{ live: true }` or `{ preview: true }` as the last argument to target
 * only specific iframe types.
 *
 * @param {string} action - The socket event name (e.g. 'timeline', 'settings', 'onReceiveAction')
 * @param {...any} args - Arguments to pass to the socket handler. If the last argument
 *   is an object with `$frameTarget` set, it's used to filter which iframes receive the message.
 */
export default function sendToPreviewIframes(action, ...args) {
  const payload = args.slice();

  // Check if last arg is frame target filter
  let targetLive = true;
  let targetPreview = true;

  const lastArg = payload[payload.length - 1];
  if (lastArg && lastArg.$frameTarget) {
    targetLive = !!lastArg.live;
    targetPreview = !!lastArg.preview;
    payload.pop();
  }

  const selectors = [];

  if (targetLive) {
    selectors.push('[data-test="producer-live-iframe"]');
  }

  if (targetPreview) {
    selectors.push('[data-test="producer-preview-iframe"]');
  }

  if (!selectors.length) { return; }

  const iframes = document.querySelectorAll(selectors.join(','));

  if (!iframes.length) { return; }

  // Deep clone args to strip Vuex reactive proxies so postMessage can serialize them
  const message = { action, args: JSON.parse(JSON.stringify(payload)) };

  iframes.forEach((iframe) => {
    if (iframe.contentWindow) {
      // Derive target origin from the iframe's src to avoid using the wildcard
      const targetOrigin = (() => {
        try {
          const url = new URL(iframe.src, window.location.origin);
          return `${url.protocol}//${url.host}`;
        } catch (e) {
          return '*';
        }
      })();

      iframe.contentWindow.postMessage(message, targetOrigin);
    }
  });
}
